/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  PRMarinaNodeArtboard,
  PRMarinaLayer,
  PRMarinaLayerStyle,
  PRMarinaNode,
  PRMarinaRect,
  PRStackPadding,
} from '@sketch-hq/sketch-web-renderer'
import {
  ArtboardPreset,
  GridSettings,
  LayerStyle,
  ElementType,
  LayoutSettings,
  SketchFrameElement,
  SketchElement,
  SketchElementBase,
  SketchElementType,
  SketchLayerElement,
  LayerStyleProperties,
  GroupStyleProperties,
  SketchSymbolMasterFrameElement,
  SketchSymbolInstanceLayerElement,
  SketchLayerElementBase,
  TextLayerStyleProperties,
  WithPotentialSharedStyle,
  WithSharedStyle,
} from '../../../../../../inspector'
import {
  transformAttributedString,
  transformCorners,
  transformExportFormatsComponent,
  transformPresentationAppearance,
  transformPresentationBlur,
  transformPresentationBorderOptions,
  transformPresentationBorders,
  transformPresentationFills,
  transformPresentationTints,
  transformPresentationShadows,
  transformSharedStyleReference,
  transformSymbolMasterReference,
  transformTextAttributes,
} from './presentationFileTransformUtils'
import {
  mapCrossAxisAlignment,
  mapDirection,
  mapLayerType,
  mapMainAxisAlignment,
  mapVerticalTextAlignment,
} from './presentationFileTransformUtils/presentationMarinaEnumsMapping'
import { computeSymbolMasterNames } from './utils'
import { StackProperties } from '../../../../../../inspector/types/InspectorTypes/Stack'

/** Props needed to create the element node */
type CreateSketchElementContext = {
  parentSymbolLayerElement?: SketchSymbolInstanceLayerElement
  artboardAncestorElement?: SketchElement
  /**
   * The bounds of the container node that can be Page is we are looking at the page view
   * or artboard if we are looking at an individual artboard.
   */
  containerNodeBounds: PRMarinaRect
  isMaskedByOtherLayer: boolean
  isInsideLockedParent: boolean
  isInsideGroupWithExports: boolean
  isInsideGroupWithStyles: boolean
}

export function createSketchElement(
  marinaNode: PRMarinaNode,
  context: CreateSketchElementContext
): SketchElement {
  if (marinaNode.isPage()) {
    return {
      ...createSketchLayerElement(marinaNode, context),
      type: ElementType.Page,
    }
  }

  if (marinaNode.isFrame() || marinaNode.isTopLevelFrame()) {
    return createSketchFrameElement(marinaNode, context)
  }

  return createSketchLayerElement(marinaNode, context)
}

export function getSketchElementBaseProperties(
  marinaNode: PRMarinaNode,
  context: Pick<
    CreateSketchElementContext,
    | 'containerNodeBounds'
    | 'parentSymbolLayerElement'
    | 'artboardAncestorElement'
  >
): SketchElementBase {
  const {
    containerNodeBounds,
    parentSymbolLayerElement,
    artboardAncestorElement,
  } = context
  const prRectBounds = marinaNode.getAbsoluteLayerBounds()
  const prMarinaLayer = marinaNode.getLayer()
  const layerSize = prMarinaLayer?.bounds
  const elementUUID = marinaNode.getLayerUUID()

  const exportsProps = getExportsProps(
    marinaNode,
    Boolean(parentSymbolLayerElement)
  )

  return {
    prMarinaNode: marinaNode,
    identifier: marinaNode.getIdentifier(),
    name: marinaNode.getLayerName(),
    elementUUID: elementUUID,
    children: [],
    // We want the bounds to be relative to the artboard and not the canvas if the root of the tree is just an artboard.
    rootRelativeBoxRect: {
      x: prRectBounds.x - containerNodeBounds.x,
      y: prRectBounds.y - containerNodeBounds.y,
      width: prRectBounds.width,
      height: prRectBounds.height,
    },
    layerSize: {
      width: layerSize?.width ?? 0,
      height: layerSize?.height ?? 0,
      widthBehaviour: prMarinaLayer?.widthSizingBehaviour,
      heightBehaviour: prMarinaLayer?.heightSizingBehaviour,
    },
    artboardAncestorElement,
    ...exportsProps,
  }
}

function getExportsProps(
  marinaNode: PRMarinaNode,
  isInsideSymbolInstance: boolean
): Pick<SketchElementBase, 'isExportable' | 'exportFormats' | 'exportId'> {
  // Exports is not supported inside symbol instances. This could potentially
  // be supported in the future but in the current state, the data is present
  // in the presentation file but we want to ignore it. The main reasons are:
  // - This is the legacy behavior. We don't want to change it for now.
  // - We would need to adapt the code to be able to fetch assets from a different document
  // when the symbol is external
  if (isInsideSymbolInstance) {
    return {
      exportFormats: [],
      isExportable: false,
      exportId: undefined,
    }
  }

  const prExportFormats = marinaNode.getExportFormats()
  const nodeUUID = marinaNode.getLayerUUID()

  const exportFormats = prExportFormats
    ? transformExportFormatsComponent(prExportFormats)
    : []
  const isExportable = exportFormats.length > 0
  const exportId = nodeUUID

  return {
    exportFormats,
    isExportable,
    exportId,
  }
}

/** ARTBOARD */

export function createSketchFrameElement(
  marinaNode: PRMarinaNode,
  /**
   * The bounds of the root artboard.
   */
  context: CreateSketchElementContext
): SketchFrameElement {
  const prMarinaFrame = marinaNode.getFrame()
  const prMarinaLayer = marinaNode.getLayer()

  if (!prMarinaFrame) {
    throw new Error('Artboard component must be present')
  }

  if (!prMarinaLayer) {
    throw new Error('LayerInfo component must be present')
  }

  const type = mapLayerType(prMarinaLayer.type.value)

  if (type !== ElementType.Artboard && type !== ElementType.SymbolMaster) {
    throw new Error('The layer type must be Artboard or SymbolMaster')
  }

  const style = getLayerStyle(prMarinaLayer, type)

  const artboardElement = {
    ...getSketchElementBaseProperties(marinaNode, {
      containerNodeBounds: context.containerNodeBounds,
    }),
    identifier: marinaNode.getIdentifier(),
    gridSettings: getGridSettings(prMarinaFrame),
    layoutSettings: getLayoutSettings(prMarinaFrame),
    preset: getArtboardPreset(prMarinaFrame),
    hasFragmentPending: marinaNode.hasFragmentPending(),
    stack: getStackProperties(marinaNode),
    artboardAncestorElement: context.artboardAncestorElement,
  }

  if (type === ElementType.Artboard) {
    return {
      ...artboardElement,
      type: ElementType.Artboard,
      style: style as WithPotentialSharedStyle<LayerStyleProperties>,
    }
  }

  const symbolMasterElement: SketchSymbolMasterFrameElement = {
    ...artboardElement,
    type: ElementType.SymbolMaster,
    ...computeSymbolMasterNames(artboardElement.name),
    style: style as WithPotentialSharedStyle<LayerStyleProperties>,
  }

  return symbolMasterElement
}

function getGridSettings(prMarinaArtboard: PRMarinaNodeArtboard): GridSettings {
  const defaultGridSettings: GridSettings = {
    isEnabled: false,
    size: 20,
    thickLineStep: 10,
  }
  const prArtboardGrid = prMarinaArtboard.getGrid()

  const gridSettings: GridSettings | undefined = prArtboardGrid
    ? {
        isEnabled: prArtboardGrid.isEnabled,
        size: prArtboardGrid.size,
        thickLineStep: prArtboardGrid.thickGridTimes,
      }
    : undefined

  return {
    ...defaultGridSettings,
    ...gridSettings,
  }
}

function getLayoutSettings(
  prMarinaArtboard: PRMarinaNodeArtboard
): LayoutSettings {
  const prArtboardLayout = prMarinaArtboard.getLayout()
  const defaultLayoutSettings: LayoutSettings = {
    columnWidth: 60,
    drawHorizontal: false,
    drawHorizontalLines: false,
    drawVertical: true,
    gutterHeight: 12,
    gutterWidth: 20,
    guttersOutside: true,
    horizontalOffset: 0,
    isEnabled: false,
    numberOfColumns: 5,
    numberOfRows: 0,
    rowHeightMultiplication: 5,
    totalWidth: 0,
  }

  const layoutSettings: LayoutSettings | undefined = prArtboardLayout
    ? {
        columnWidth: prArtboardLayout.columnWidth,
        drawHorizontal: prArtboardLayout.drawHorizontal,
        drawHorizontalLines: prArtboardLayout.drawHorizontalLines,
        drawVertical: prArtboardLayout.drawVertical,
        gutterHeight: prArtboardLayout.gutterHeight,
        gutterWidth: prArtboardLayout.gutterWidth,
        guttersOutside: prArtboardLayout.gutterOutside,
        horizontalOffset: prArtboardLayout.horizontalOffset,
        isEnabled: prArtboardLayout.isEnabled,
        numberOfColumns: prArtboardLayout.columnsCount,
        numberOfRows: 0,
        rowHeightMultiplication: prArtboardLayout.rowHeightMultiplication,
        totalWidth: prArtboardLayout.totalWidth,
      }
    : undefined

  return {
    ...defaultLayoutSettings,
    ...layoutSettings,
  }
}

function getArtboardPreset(
  prMarinaArtboard: PRMarinaNodeArtboard
): ArtboardPreset {
  const presetBounds = prMarinaArtboard.templateBounds

  return {
    name: prMarinaArtboard.templateName,
    width: presetBounds.width,
    height: presetBounds.height,
  }
}

function getStackPadding(padding: PRStackPadding) {
  return {
    top: padding.top,
    right: padding.right,
    bottom: padding.bottom,
    left: padding.left,
  }
}

function getStackProperties(marinaNode: PRMarinaNode): StackProperties | null {
  const stack = marinaNode.getStack()
  const stackItem = marinaNode.getStackItem()

  if (!stack) {
    return null
  }

  return {
    direction: mapDirection(stack.direction.value),
    gap: stack.gap,
    crossAxisAlignment: mapCrossAxisAlignment(stack.crossAxisAlignment.value),
    mainAxisAlignment: mapMainAxisAlignment(stack.mainAxisAlignment.value),
    padding: getStackPadding(stack.padding),
  }
}

/** LAYER */

export function createSketchLayerElement(
  marinaNode: PRMarinaNode,
  context: CreateSketchElementContext
): SketchLayerElement {
  const {
    isMaskedByOtherLayer,
    containerNodeBounds,
    parentSymbolLayerElement,
    artboardAncestorElement,
  } = context
  const prMarinaLayer = marinaNode.getLayer()

  if (!prMarinaLayer) {
    throw Error('LayerInfo component must be present')
  }

  const type = mapLayerType(prMarinaLayer.type.value)

  if (
    type === ElementType.Artboard ||
    type === ElementType.SymbolMaster ||
    type === ElementType.Page ||
    type === ElementType.Undefined
  ) {
    throw Error(
      `Illegal layer type "${type}" encountered while creating SketchLayerElement. (Layer name: ${marinaNode.getName()}, uuid: ${marinaNode.getLayerUUID()}, Artboard ancestor: ${
        artboardAncestorElement?.name
      })`
    )
  }

  const style = getLayerStyle(prMarinaLayer, type)

  const prCorners = prMarinaLayer.getCorners()
  const corners = prCorners ? transformCorners(prCorners) : undefined

  const prAttributedString = prMarinaLayer.getAttributedString()
  const text = prAttributedString
    ? transformAttributedString(prAttributedString)
    : undefined

  const isLocked = prMarinaLayer.locked || context.isInsideLockedParent
  // Omit type because we don't know yet if it's a symbol instance or not yet.
  const layerElement: SketchLayerElementBase = {
    ...getSketchElementBaseProperties(marinaNode, {
      containerNodeBounds,
      parentSymbolLayerElement: parentSymbolLayerElement,
      artboardAncestorElement,
    }),
    isLayerMasked: isMaskedByOtherLayer,
    parentSymbolLayerElement: parentSymbolLayerElement,
    corners,
    rotation: prMarinaLayer.rotation,
    appearance: style?.appearance,
    text,
    isLocked,
    isInsideGroupWithExports: context.isInsideGroupWithExports,
    isInsideGroupWithStyles: context.isInsideGroupWithStyles,
  }

  if (type === ElementType.Hotspot || type === ElementType.Slice) {
    return { ...layerElement, type, style: undefined }
  }

  if (type === ElementType.Group) {
    return {
      ...layerElement,
      type,
      style: style as GroupStyleProperties,
    }
  }

  if (type === ElementType.Text) {
    return {
      ...layerElement,
      type,
      style: style as WithPotentialSharedStyle<TextLayerStyleProperties>,
    }
  }

  if (type === ElementType.SymbolInstance) {
    const prSymbolMasterReference = marinaNode.getSymbolMasterReference()

    if (!prSymbolMasterReference) {
      throw Error(
        'SymbolInstance element encountered without a SymbolMasterReference'
      )
    }

    return {
      ...layerElement,
      type,
      style: style as GroupStyleProperties,
      symbolMaster: transformSymbolMasterReference(prSymbolMasterReference),
    }
  }

  return {
    ...layerElement,
    type,
    style: style as WithPotentialSharedStyle<LayerStyleProperties>,
  }
}

/* UTILS */

function getLayerStyle(
  prMarinaLayer: PRMarinaLayer,
  type: SketchElementType
): LayerStyle | undefined {
  const prStyle = prMarinaLayer.getStyle()
  const prLayerStyle = prStyle?.getLayerStyle()

  if (!prStyle || !prLayerStyle) {
    return
  }

  const prOriginalSharedStyleValues = prStyle.getOriginalSharedStyleValues()
  const prSharedStyleReference = prStyle.getSharedStyleReference()
  const isDirty = prStyle.isDirty

  const basicStyleProps = getStyleProperties(prLayerStyle)

  const originalSharedStyleValues = prOriginalSharedStyleValues
    ? getStyleProperties(prOriginalSharedStyleValues)
    : undefined

  const sharedStyleReference = prSharedStyleReference
    ? transformSharedStyleReference(prSharedStyleReference)
    : undefined

  if (type === ElementType.Group) {
    const groupStyle: GroupStyleProperties = {
      isDirty,
      fills: basicStyleProps.fills ?? [],
      tints: basicStyleProps.tints ?? [],
      shadows: basicStyleProps.shadows ?? [],
      appearance: basicStyleProps.appearance,
    }

    return groupStyle
  }

  if (sharedStyleReference) {
    const styleWithSharedStyle: WithSharedStyle<LayerStyleProperties> = {
      ...basicStyleProps,
      isDirty,
      originalSharedStyleValues,
      sharedStyleReference,
    }
    return styleWithSharedStyle
  }

  return basicStyleProps
}

export function getStyleProperties(prMarinaLayerStyle: PRMarinaLayerStyle) {
  const prBlur = prMarinaLayerStyle.getBlur()
  const prFills = prMarinaLayerStyle.fills
  const prTints = prMarinaLayerStyle.tints
  const prAppearance = prMarinaLayerStyle.appearance
  const prBorders = prMarinaLayerStyle.borders
  const prShadows = prMarinaLayerStyle.shadows
  const prBorderOptions = prMarinaLayerStyle.borderOptions
  const prInnerShadows = prMarinaLayerStyle.innerShadows
  const prVerticalTextAlignment = prMarinaLayerStyle.verticalTextAlignment
  const prText = prMarinaLayerStyle.getTextAttributes()

  const tints = prTints ? transformPresentationTints(prTints) : []
  const fills = prFills ? transformPresentationFills(prFills) : []
  const blur = prBlur ? transformPresentationBlur(prBlur) : undefined
  const appearance = prAppearance
    ? transformPresentationAppearance(prAppearance)
    : undefined
  const shadows = prShadows ? transformPresentationShadows(prShadows) : []
  const borders = prBorders ? transformPresentationBorders(prBorders) : []
  const borderOptions = prBorderOptions
    ? transformPresentationBorderOptions(prBorderOptions)
    : undefined
  const innerShadows = prInnerShadows
    ? transformPresentationShadows(prInnerShadows)
    : []
  const verticalAlignment = mapVerticalTextAlignment(
    prVerticalTextAlignment.value
  )
  const text = prText ? transformTextAttributes(prText)?.attributes : undefined

  return {
    tints,
    fills,
    blur,
    appearance,
    borders,
    borderOptions,
    shadows,
    innerShadows,
    verticalAlignment,
    text,
  } as const
}
